import { createSlice, createSelector } from '@reduxjs/toolkit';
import { logout } from "../common/reducers/initContext";
import { dataHookFactory } from '../common/reducers/utilities';
import { ensureArray } from '../common/utils/arrayUtilities';

const compareLocations = (l1, l2) => {
    return l1.internalid === l2.internalid;
}

const locationSlice = createSlice({
    name: 'locations',
    initialState: {
        data: []
    },
    reducers: {
        set: (state, action) => {
            state.data = action.payload
        },
        addOrUpdate: (state, action) => {
            for (const l of ensureArray(action.payload)) {
                let idx = state.data.findIndex(l2 => compareLocations(l, l2));
                if (idx >= 0) {
                    state.data.splice(idx, 1, l);
                } else {
                    state.data.push(l);
                }
            }
        },
        remove: (state, action) => {
            for (const l of ensureArray(action.payload)) {
                let idx = state.data.findIndex(l2 => compareLocations(l2, l));
                if (idx >= 0) {
                    state.data.splice(idx, 1);
                }
            }
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => locationSlice.getInitialState());
    }
});

// #region network operations
const fetchLocations = network => network.get('/api/v1/locations', locationSlice.actions.set);
export const updateLocation = (network, location) => network.put('/api/v1/location', location, locationSlice.actions.addOrUpdate);
export const createLocation = (network, location) => network.post('/api/v1/location', location, locationSlice.actions.addOrUpdate);
export const removeLocation = (network, internalid) => network.delete('/api/v1/location/' + encodeURIComponent(internalid), locationSlice.actions.remove);
// #endregion

// #region selectors
const selectLocations = state => state.locations.data;

// we now make a reselect to memoize a select function parameterized by jobid and expose that factory through a convenience function
const selectLocationBySiteLevelAndType = createSelector([selectLocations, (locations, site) => site, (locations, site, level) => level, (locations, site, level, type) => type], (locations, site, level, type) => {
    return locations.filter(p => (site === undefined || p.site === null || p.site === site) && (level === undefined || p.level === null || p.level === level) && (type === undefined || p.type === null || p.type === type))
});
const selectLocationForSiteLevelType = (site, level, type) => state => selectLocationBySiteLevelAndType(state, site, level, type);
// #endregion

// #region listeners
const listen = {
    locationCreated: (location, { dispatch }) => dispatch(locationSlice.actions.addOrUpdate(location)),
    locationUpdated: (location, { dispatch }) => dispatch(locationSlice.actions.addOrUpdate(location)),
    locationRemoved: (location, { dispatch }) => dispatch(locationSlice.actions.remove(location))
}
// #endregion

// #region permission checks
export const canInsertLocation = (credentials, location) => credentials.access.hasFull('administrator');
export const canUpdateLocation = canInsertLocation;
export const canRemoveLocation = canInsertLocation;
// #endregion

export const [useLocations] = dataHookFactory(selectLocations, { run: fetchLocations, listen });
export const [useLocationsForSiteLevelType] = dataHookFactory(selectLocationForSiteLevelType, () => ({ run: fetchLocations, listen }));

const reducerDetails = { name: locationSlice.name, reducer: locationSlice.reducer };
export default reducerDetails;